import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { DocumentViewer } from '@/features/documents/preview/components/document-viewer';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { useState, Suspense } from 'react';
import { useLocale } from 'use-intl';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Breadcrumb, BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderButton } from '@/ui/components/header';
import {
  Sidenav,
  SidenavButton,
  SidenavEmpty,
  SidenavHeader,
  SidenavHeading,
  SidenavItem,
  SidenavList,
} from '@/ui/components/sidenav';
import { Icon } from '@/ui/components/icon';
import {
  AiBrain02Icon,
  AlertCircleIcon,
  Download01Icon,
  Globe02Icon,
  Upload01Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { organizationKnowledgeBasesQuery } from '@/features/knowledge-base/queries';
import { isStandard } from '@/features/knowledge-base/utils';
import { getCountryLabel } from '@/features/projects/countries';
import { CountryFlag } from '@/ui/components/country-flag';
import { entityDocumentsQuery, useDocumentsIndexingStatus } from '@/features/documents/queries';
import { KnowledgeBaseType } from '@/features/knowledge-base/constants';
import { BadgeDot } from '@/ui/components/badge';
import { dateFormat, formatDate } from '@/dates';
import { Spinner } from '@/ui/components/spinner';
import { MainScrollArea } from '@/ui/components/main';
import { FileUploadDialog } from '@/features/documents/components/file-upload';
import { fileUploadOpenAtom } from '@/features/documents/store';
import { useSetAtom } from 'jotai';
import { Button } from '@/ui/components/button';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/ui/components/tooltip';
import { downloadFileFromUrl, getDocumentFailureMessage } from '@/features/documents/utils';
import { Alert, AlertDescription } from '@/ui/components/alert';

export const Route = createFileRoute('/_authed/settings/knowledge-base/internal/$knowledgeBaseId')({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const knowledgeBases = await context.queryClient.query({
      ...organizationKnowledgeBasesQuery(context.auth.user.organizationId),
      staleTime: 'static',
    });
    if (!knowledgeBases.some((kb) => kb.id === params.knowledgeBaseId)) throw notFound();
    await context.queryClient.query({
      ...entityDocumentsQuery(params.knowledgeBaseId, 'internalKnowledgeBase'),
      staleTime: 'static',
    });
  },
});

function RouteComponent() {
  const { knowledgeBaseId } = Route.useParams();
  const locale = useLocale();
  const auth = useAuth();
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const setUploadOpen = useSetAtom(fileUploadOpenAtom);

  const { data: documents } = useSuspenseQuery(
    entityDocumentsQuery(knowledgeBaseId, 'internalKnowledgeBase'),
  );
  const { data: knowledgeBases } = useSuspenseQuery(
    organizationKnowledgeBasesQuery(auth.user.organizationId),
  );
  const knowledgeBase = knowledgeBases.find((kb) => kb.id === knowledgeBaseId);

  const { areDocumentsIndexed } = useDocumentsIndexingStatus({
    documentType: 'internalKnowledgeBase',
    id: knowledgeBaseId,
  });

  const effectiveDocumentId = selectedDocumentId ?? documents[0]?.id;
  const selectedDocument = documents.find((doc) => doc.id === effectiveDocumentId);

  if (!knowledgeBase) throw notFound();

  const tab =
    knowledgeBase.type === KnowledgeBaseType.INTERNAL_HANDBOOK
      ? 'internal_handbook'
      : knowledgeBase.type === KnowledgeBaseType.COMPANY_DOCUMENT
        ? 'company_documents'
        : 'internal';

  return (
    <>
      <Header>
        <HeaderBackButton to="/settings/knowledge-base" search={{ tab }} />
        <Breadcrumb>
          <BreadcrumbLink to={'/settings/knowledge-base'} search={{ tab }}>
            <Icon icon={AiBrain02Icon} />
            {t({ en: 'Knowledge base', nb: 'Kunnskapsbase' })}
          </BreadcrumbLink>
          <BreadcrumbLink
            to={'/settings/knowledge-base/internal/$knowledgeBaseId'}
            params={{ knowledgeBaseId }}
          >
            {knowledgeBase.name}
          </BreadcrumbLink>
          {selectedDocument && <BreadcrumbPage>{selectedDocument.name}</BreadcrumbPage>}
        </Breadcrumb>
        {selectedDocument && (
          <HeaderButton
            onClick={() => downloadFileFromUrl(selectedDocument.sasUri, selectedDocument.name)}
            aria-label="Download"
          >
            <Icon icon={Download01Icon} />
          </HeaderButton>
        )}
      </Header>
      <MainScrollArea viewportClassName="flex-col @3xl:flex-row flex grow">
        <Sidenav className="border-b @3xl:border-b-0 @3xl:border-r">
          <SidenavHeader>
            <SidenavHeading>
              {t({ en: 'Number of versions:', nb: 'Antall versjoner:' })}{' '}
              <strong className="text-foreground">{documents.length}</strong>
            </SidenavHeading>
            <Tooltip>
              <TooltipTrigger
                delay={0}
                render={
                  <Button
                    className="-mr-1.5"
                    kind="icon"
                    variant="ghost"
                    onClick={() => setUploadOpen(true)}
                    disabled={!areDocumentsIndexed}
                    aria-label="Upload documents"
                  >
                    <Icon icon={Upload01Icon} />
                  </Button>
                }
              />
              {!areDocumentsIndexed && (
                <TooltipPopup>
                  {t({
                    en: 'Please wait until files are analysed before uploading more.',
                    nb: 'Vent til filene er analysert før du laster opp flere.',
                  })}
                </TooltipPopup>
              )}
            </Tooltip>
          </SidenavHeader>
          <SidenavList>
            {documents.length === 0 ? (
              <SidenavEmpty>{t({ en: 'No documents', nb: 'Ingen dokumenter' })}</SidenavEmpty>
            ) : (
              documents.map((doc) => (
                <SidenavItem key={doc.id} className="@3xl:w-full">
                  <SidenavButton
                    active={effectiveDocumentId === doc.id}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedDocumentId(doc.id);
                    }}
                  >
                    <span className="@3xl:line-clamp-1">{doc.name}</span>
                    {doc.indexingError && <BadgeDot variant="destructive" />}
                  </SidenavButton>
                </SidenavItem>
              ))
            )}
          </SidenavList>
        </Sidenav>
        <div className="grow flex flex-col min-w-0 relative">
          {selectedDocument ? (
            <>
              <ul className="flex gap-2 px-3 items-center h-11 border-b scrollbar-hidden overflow-x-auto text-sm whitespace-nowrap">
                <li>{selectedDocument.sizeInMegaBytes.toFixed(2)} MB</li>{' '}
                <span className="text-muted">•</span>{' '}
                {selectedDocument.indexingError ? (
                  <li className="flex items-center gap-1.5">
                    <BadgeDot variant="destructive" />
                    {t(common.failed)}
                  </li>
                ) : (
                  <li className="flex items-center gap-1.5">
                    <BadgeDot variant={areDocumentsIndexed ? 'success' : 'warning'} />
                    {areDocumentsIndexed
                      ? t({ en: 'Analysed', nb: 'Analysert' })
                      : t({ en: 'Analysis pending', nb: 'Venter på analyse' })}
                  </li>
                )}
                <span className="text-muted">•</span>{' '}
                <li>
                  {t({ en: 'Added', nb: 'Lagt til' })}{' '}
                  {selectedDocument.createdAt &&
                    formatDate(selectedDocument.createdAt, dateFormat.dayFirst, locale)}
                </li>
                {isStandard(knowledgeBase.type) && (
                  <>
                    <span className="text-muted">•</span>{' '}
                    <li className="flex items-center gap-1.5">
                      {knowledgeBase.countryCode ? (
                        <>
                          <CountryFlag code={knowledgeBase.countryCode} />
                          {getCountryLabel(knowledgeBase.countryCode, locale)}
                        </>
                      ) : (
                        <>
                          <Icon icon={Globe02Icon} className="size-4 shrink-0 text-muted" />
                          {t({ en: 'International', nb: 'Internasjonal' })}
                        </>
                      )}
                    </li>
                  </>
                )}
              </ul>
              {selectedDocument.indexingError && (
                <Alert
                  variant="destructive"
                  size="sm"
                  className="rounded-none border-x-0 border-t-0"
                >
                  <Icon icon={AlertCircleIcon} />
                  <AlertDescription>
                    {getDocumentFailureMessage(selectedDocument.failureReason)}
                  </AlertDescription>
                </Alert>
              )}
              <div className="relative grow flex flex-col min-h-0">
                <Suspense fallback={<Spinner kind="inset" />}>
                  <DocumentViewer
                    url={selectedDocument.sasUri}
                    extension={selectedDocument.extension}
                  />
                </Suspense>
              </div>
            </>
          ) : (
            <p className="text-muted absolute inset-0 m-auto size-fit">
              {t({
                en: 'Select a document to view details',
                nb: 'Velg et dokument for å se detaljer',
              })}
            </p>
          )}
        </div>
      </MainScrollArea>
      <FileUploadDialog resourceId={knowledgeBaseId} resourceType="internalKnowledgeBase" />
    </>
  );
}
